import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {map, shareReplay} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PageDataService {
  private readonly SIDENAV_STATE_KEY = 'sidenavOpenState';
  private readonly SIDENAV_COLLAPSED_KEY = 'sidenavCollapsedState';
  
  sidenavBackdropVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  sidenavHasBackdrop$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  
  // sidenavOpen$ controls visibility (mostly for mobile)
  sidenavOpen$: BehaviorSubject<boolean> = new BehaviorSubject(
    this.getSavedSidenavState()
  );

  // isCollapsed$ controls width (mini vs full)
  isCollapsed$: BehaviorSubject<boolean> = new BehaviorSubject(
    this.getSavedCollapsedState()
  );

  isHandset$ = this.breakpointObserver.observe('(max-width: 1024px)')
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  showSidenav$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  showFooter$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  showHeader$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(private breakpointObserver: BreakpointObserver) {
    this.isHandset$.subscribe(isHandset => {
      if (isHandset) {
        // On small screens, keep sidenav open but collapsed (showing only icons)
        this.sidenavOpen$.next(true);
        this.isCollapsed$.next(true);
      } else {
        // On desktop, start open (but respect saved collapsed state)
        this.sidenavOpen$.next(true);
        this.isCollapsed$.next(this.getSavedCollapsedState());
      }
      this.updateBackdropState();
    });
      
    // Save state changes to localStorage
    this.sidenavOpen$.subscribe(open => {
      localStorage.setItem(this.SIDENAV_STATE_KEY, JSON.stringify(open));
      this.updateBackdropState();
    });

    this.isCollapsed$.subscribe(collapsed => {
      localStorage.setItem(this.SIDENAV_COLLAPSED_KEY, JSON.stringify(collapsed));
      this.updateBackdropState();
    });
  }

  private updateBackdropState(): void {
    let isHandset = false;
    this.isHandset$.subscribe(val => isHandset = val).unsubscribe();
    const isOpen = this.sidenavOpen$.value;
    const isCollapsed = this.isCollapsed$.value;
    this.sidenavBackdropVisible$.next(isHandset && isOpen && !isCollapsed);
  }

  private getSavedSidenavState(): boolean {
    const savedState = localStorage.getItem(this.SIDENAV_STATE_KEY);
    return savedState ? JSON.parse(savedState) : true;
  }

  private getSavedCollapsedState(): boolean {
    const savedState = localStorage.getItem(this.SIDENAV_COLLAPSED_KEY);
    return savedState ? JSON.parse(savedState) : false;
  }

  toggleSidenav(): void {
    let isHandset = false;
    this.isHandset$.subscribe(val => isHandset = val).unsubscribe();
    if (isHandset) {
      // On small screens, force collapsed and open (showing only icons)
      this.sidenavOpen$.next(true);
      this.isCollapsed$.next(true);
    } else {
      // If it's closed, open it. If it's open, toggle collapse.
      if (!this.sidenavOpen$.value) {
        this.sidenavOpen$.next(true);
      } else {
        this.isCollapsed$.next(!this.isCollapsed$.value);
      }
    }
  }

  openSidenav(): void {
    this.sidenavOpen$.next(true);
  }

  expandSidenav(): void {
    this.isCollapsed$.next(false);
  }

  closeSidenav(): void {
    this.sidenavOpen$.next(false);
  }
}